/**
 * Floating help-card primitive used by the new-space form's per-field tip
 * popovers.
 *
 * A positioned card that hangs to the right of the form (`marginRight: -336`)
 * and accepts a title plus arbitrary children. Callers provide `topOffset` so
 * the card aligns with the currently focused field. `PLAYBOOK_URL` is exported
 * here as the canonical link target for Playbook references across
 * editorial-dash help content, so relocating the documentation only requires a
 * single edit.
 */

import { useTheme } from '@repo/theme';
import { createThemedStyles } from '@repo/theme/styles';
import { HorizBox, Pad } from 'component/basics';
import { Heading } from 'component/text';
import React from 'react';
import { View } from 'react-native';
import type { TLabel } from 'util/stdtypes';

export const PLAYBOOK_URL =
    'https://newpublic.notion.site/Public-Spaces-Incubator-Editorial-Documentation-295fbd65fb3e809eb858ca45b3d9b556';

export type FormFieldHelpProps = {
    titleLabel: TLabel;
    topOffset?: number;
    children: React.ReactNode;
};

export function FormFieldHelp({ titleLabel, topOffset, children }: FormFieldHelpProps) {
    const { colors } = useTheme();
    const s = useFormFieldHelpStyle();
    return (
        <View style={[s.container, topOffset !== undefined && { top: topOffset }]}>
            <HorizBox center gap={6}>
                <Heading text='💡' type='large' />
                <Heading color={colors.colorPrimaryBrandIris} label={titleLabel} type='small' weight='medium' />
            </HorizBox>
            <Pad size={24} />
            {children}
        </View>
    );
}

const useFormFieldHelpStyle = createThemedStyles((tokens) => ({
    container: {
        backgroundColor: tokens.colors.colorSurfacePrimary,
        borderRadius: 16,
        padding: 20,
        boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.1)',
        width: 312,
        position: 'absolute',
        right: 0,
        zIndex: 1,
        marginRight: -336,
    },
}));
